import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Pencil, X, Loader2, Users, FileText } from "lucide-react";
import { BLOG_CATEGORIES } from "@/data/site";
import { RichTextEditor, uploadImage } from "@/components/RichTextEditor";

const EMPTY = { title: "", category: "Amazon Strategy", excerpt: "", content: "", cover_image: "", author: "DNA eCommerce Ads", published: true };

export default function Admin() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | post object
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);

  useEffect(() => {
    if (ready && (!user || user === false)) navigate("/login");
  }, [ready, user, navigate]);

  const load = useCallback(async () => {
    try {
      const [l, p] = await Promise.all([api.get("/admin/leads"), api.get("/admin/blog")]);
      setLeads(l.data);
      setPosts(p.data);
    } catch {
      /* handled by redirect */
    }
  }, []);

  useEffect(() => {
    if (user && user !== false) load();
  }, [user, load]);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...p }); setEditing(p); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { title: form.title, category: form.category, excerpt: form.excerpt, content: form.content, cover_image: form.cover_image, author: form.author, published: form.published };
      if (editing === "new") await api.post("/admin/blog", payload);
      else await api.put(`/admin/blog/${editing.id}`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/${id}`);
    toast.success("Deleted");
    load();
  };

  const onCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setCoverUploading(true);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, cover_image: url }));
      toast.success("Cover uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setCoverUploading(false);
    }
  };

  const setLeadStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/admin/leads/${id}`);
    setLeads((ls) => ls.filter((l) => l.id !== id));
    toast.success("Lead deleted");
  };

  if (!ready || !user || user === false) {
    return <div className="flex min-h-screen items-center justify-center bg-ink text-muted">Loading…</div>;
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls = "w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-muted outline-none focus:border-orange";

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-page">
      <header className="sticky top-0 z-40 border-b border-white/8 bg-ink/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <img src="/dna-logo.png" alt="logo" className="h-8 w-auto" />
            <span className="font-display font-semibold">Admin</span>
          </div>
          <button onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn" className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2 text-sm text-muted hover:text-white">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-8">
        <div className="mb-8 flex gap-3">
          <button onClick={() => setTab("leads")} data-testid="tab-leads" className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium ${tab === "leads" ? "bg-orange text-white" : "border border-white/15 text-muted"}`}>
            <Users className="h-4 w-4" /> Leads ({leads.length})
          </button>
          <button onClick={() => setTab("blog")} data-testid="tab-blog" className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium ${tab === "blog" ? "bg-orange text-white" : "border border-white/15 text-muted"}`}>
            <FileText className="h-4 w-4" /> Blog ({posts.length})
          </button>
        </div>

        {tab === "leads" && (
          <div className="overflow-x-auto rounded-xl border border-white/8" data-testid="leads-table">
            <table className="w-full text-left text-sm">
              <thead className="bg-white/[0.03] text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Company</th><th className="px-4 py-3">Marketplace</th>
                  <th className="px-4 py-3">Message</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-muted">No leads yet.</td></tr>
                )}
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-white/6" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3 font-medium text-white">{l.name}</td>
                    <td className="px-4 py-3 text-muted">{l.email}</td>
                    <td className="px-4 py-3 text-muted">{l.company || "—"}</td>
                    <td className="px-4 py-3 text-muted">{l.marketplace || "—"}</td>
                    <td className="px-4 py-3 max-w-[220px] truncate text-muted">{l.message || "—"}</td>
                    <td className="px-4 py-3">
                      <select value={l.status} onChange={(e) => setLeadStatus(l.id, e.target.value)} className="rounded-md border border-white/10 bg-surface px-2 py-1 text-xs text-white">
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="won">Won</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-muted">{new Date(l.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => deleteLead(l.id)} data-testid={`delete-lead-${l.id}`} className="rounded-lg border border-white/15 p-2 text-muted hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "blog" && (
          <div>
            <button onClick={openNew} data-testid="new-post-btn" className="mb-5 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange to-magenta px-4 py-2.5 text-sm font-semibold text-white">
              <Plus className="h-4 w-4" /> New Post
            </button>
            <div className="grid grid-cols-1 gap-3">
              {posts.map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded-xl border border-white/8 bg-surface p-4" data-testid={`post-row-${p.id}`}>
                  <div>
                    <div className="font-display font-semibold text-white">{p.title}</div>
                    <div className="text-xs text-muted">{p.category} · {p.published ? "Published" : "Draft"}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(p)} data-testid={`edit-post-${p.id}`} className="rounded-lg border border-white/15 p-2 text-muted hover:text-white"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(p.id)} data-testid={`delete-post-${p.id}`} className="rounded-lg border border-white/15 p-2 text-muted hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid="post-editor">
          <div className="absolute inset-0 bg-black/80" onClick={() => setEditing(null)} />
          <form onSubmit={save} className="relative z-10 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-white/10 bg-surface p-7">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="font-display text-xl font-bold">{editing === "new" ? "New Post" : "Edit Post"}</h3>
              <button type="button" onClick={() => setEditing(null)}><X className="h-5 w-5 text-muted" /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="post-title-input" className={inputCls} placeholder="Title" value={form.title} onChange={set("title")} required />
              <select data-testid="post-category-input" className={inputCls} value={form.category} onChange={set("category")}>
                {BLOG_CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input className={inputCls} placeholder="Cover image URL" value={form.cover_image} onChange={set("cover_image")} />
              <div className="flex items-center gap-3">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-xs text-muted hover:text-white" data-testid="cover-upload-label">
                  {coverUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Upload cover
                  <input type="file" accept="image/*" className="hidden" onChange={onCoverUpload} data-testid="cover-upload-input" />
                </label>
                {form.cover_image && <img src={form.cover_image} alt="cover" className="h-10 w-16 rounded object-cover" />}
              </div>
              <textarea data-testid="post-excerpt-input" className={`${inputCls} resize-none`} rows={2} placeholder="Excerpt" value={form.excerpt} onChange={set("excerpt")} required />
              <div>
                <label className="mb-1 block text-xs uppercase tracking-wide text-muted">Content</label>
                <RichTextEditor key={editing === "new" ? "new" : editing.id} value={form.content} onChange={(html) => setForm((f) => ({ ...f, content: html }))} />
              </div>
              <label className="flex items-center gap-2 text-sm text-muted">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} /> Published
              </label>
            </div>
            <button data-testid="save-post-btn" disabled={saving} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-orange to-magenta px-6 py-3 text-sm font-semibold text-white disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Post"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
